import React, { useEffect, useRef } from 'react'
// @ts-expect-error - EffectContainer module doesn't exist yet, EffectProps type is unresolved
import { EffectProps } from './EffectContainer'

interface GridPoint {
    x: number
    y: number
    z: number
    wave: number
    intensity: number
    connected: number[]
}

interface WaveEntity {
    id: number
    originX: number
    originY: number
    radius: number
    maxRadius: number
    intensity: number
    color: number
    speed: number
    thickness: number
    birth: number
    directionX: number
    directionY: number
    spread: number
}

export function BubbleWaveEffect({ clickPower, canvasRef, timeRef }: EffectProps) {
    const gridRef = useRef<GridPoint[][]>([])
    const wavesRef = useRef<WaveEntity[]>([])
    const waveIdRef = useRef(0)
    const lastClickPowerRef = useRef(0)

    useEffect(() => {
        if (!canvasRef.current) return
        if (clickPower === 0) {
            // Clear waves when power is 0
            wavesRef.current = []
            return
        }

        const canvas = canvasRef.current
        const ctx = canvas.getContext('2d')
        if (!ctx) return

        // Set canvas size much larger for diffuse effect
        const size = 400 + clickPower * 30
        canvas.width = size
        canvas.height = size

        // Initialize or update grid when canvas size changes
        const cols = 25
        const rows = 25

        if (
            gridRef.current.length !== cols ||
            (gridRef.current[0] && gridRef.current[0].length !== rows)
        ) {
            gridRef.current = []

            for (let i = 0; i < cols; i++) {
                gridRef.current[i] = []
                for (let j = 0; j < rows; j++) {
                    gridRef.current[i][j] = {
                        x: (canvas.width / cols) * i + canvas.width / cols / 2,
                        y: (canvas.height / rows) * j + canvas.height / rows / 2,
                        z: 0,
                        wave: 0,
                        intensity: 0,
                        connected: [],
                    }
                }
            }
        } else {
            // Update existing grid coordinates if canvas was resized
            for (let i = 0; i < cols; i++) {
                for (let j = 0; j < rows; j++) {
                    gridRef.current[i][j].x = (canvas.width / cols) * i + canvas.width / cols / 2
                    gridRef.current[i][j].y = (canvas.height / rows) * j + canvas.height / rows / 2
                }
            }
        }

        // Create new wave when click power increases
        if (clickPower > lastClickPowerRef.current) {
            const angle = Math.random() * Math.PI * 2
            const newWave: WaveEntity = {
                id: waveIdRef.current++,
                originX: canvas.width / 2,
                originY: canvas.height / 2,
                radius: 0,
                maxRadius: Math.max(canvas.width, canvas.height) * 1.5,
                intensity: 1,
                color: Math.random() * 360,
                speed: 1.2 + Math.random() * 0.6,
                thickness: 35 + clickPower * 3,
                birth: timeRef.current,
                directionX: Math.cos(angle),
                directionY: Math.sin(angle),
                spread: 0.3 + Math.random() * 0.4,
            }

            wavesRef.current.push(newWave)

            if (wavesRef.current.length > 10) {
                wavesRef.current.shift()
            }
        }

        lastClickPowerRef.current = clickPower

        const animate = () => {
            // Update time
            timeRef.current += 0.02

            // Clear canvas completely for pure transparency
            ctx.clearRect(0, 0, canvas.width, canvas.height)

            // Update wave entities
            wavesRef.current = wavesRef.current.filter(wave => {
                wave.radius += wave.speed * 0.4
                wave.intensity *= 0.99

                wave.originX += wave.directionX * wave.speed * 0.25
                wave.originY += wave.directionY * wave.speed * 0.25

                const offCanvas =
                    wave.originX < -150 ||
                    wave.originX > canvas.width + 150 ||
                    wave.originY < -150 ||
                    wave.originY > canvas.height + 150
                return !offCanvas && wave.radius < wave.maxRadius && wave.intensity > 0.005
            })

            // Draw bubble chain directional waves
            wavesRef.current.forEach(wave => {
                const age = timeRef.current - wave.birth

                ctx.save()

                const waveAngle = Math.atan2(wave.directionY, wave.directionX)

                const hue1 = wave.color + age * 10
                const hue2 = wave.color + 30 + age * 8

                // Draw a chain of bubbles
                const bubbleCount = 10
                const maxBubbleSize = wave.thickness * 1.8
                const chainLength = wave.radius * 2.5

                for (let i = 0; i < bubbleCount; i++) {
                    const progress = i / (bubbleCount - 1)
                    const distance = wave.radius - progress * chainLength

                    // Bubble position with gentle floating motion
                    const wobbleX = Math.sin(age * 1.5 + i * 0.7) * wave.thickness * 0.2
                    const wobbleY = Math.cos(age * 1.2 + i * 0.5) * wave.thickness * 0.2

                    const bubbleX = wave.originX + Math.cos(waveAngle) * distance + wobbleX
                    const bubbleY = wave.originY + Math.sin(waveAngle) * distance + wobbleY

                    // Bubble size varies
                    const sizeFactor = 1 - progress * 0.5
                    const bubbleRadius =
                        maxBubbleSize * sizeFactor * (1 + Math.sin(age * 2 + i * 0.3) * 0.15)

                    if (bubbleRadius < 3) continue

                    // Outer soft glow
                    const glowGradient = ctx.createRadialGradient(
                        bubbleX,
                        bubbleY,
                        0,
                        bubbleX,
                        bubbleY,
                        bubbleRadius * 2.5,
                    )

                    const bubbleHue = hue1 + progress * 40
                    const bubbleIntensity = wave.intensity * (1 - progress * 0.4)

                    glowGradient.addColorStop(
                        0,
                        `hsla(${bubbleHue}, 80%, 55%, ${bubbleIntensity * 0.3})`,
                    )
                    glowGradient.addColorStop(
                        0.4,
                        `hsla(${bubbleHue + 15}, 75%, 50%, ${bubbleIntensity * 0.2})`,
                    )
                    glowGradient.addColorStop(1, `hsla(${bubbleHue + 30}, 70%, 45%, 0)`)

                    ctx.fillStyle = glowGradient
                    ctx.globalAlpha = bubbleIntensity * 0.6
                    ctx.beginPath()
                    ctx.arc(bubbleX, bubbleY, bubbleRadius * 2.5, 0, Math.PI * 2)
                    ctx.fill()

                    // Main bubble body
                    const bubbleGradient = ctx.createRadialGradient(
                        bubbleX - bubbleRadius * 0.3,
                        bubbleY - bubbleRadius * 0.3,
                        0,
                        bubbleX,
                        bubbleY,
                        bubbleRadius * 1.2,
                    )

                    bubbleGradient.addColorStop(
                        0,
                        `hsla(${bubbleHue}, 85%, 65%, ${bubbleIntensity * 0.2})`,
                    )
                    bubbleGradient.addColorStop(
                        0.7,
                        `hsla(${bubbleHue + 30}, 75%, 45%, ${bubbleIntensity * 0.5})`,
                    )
                    bubbleGradient.addColorStop(
                        1,
                        `hsla(${bubbleHue + 45}, 70%, 35%, ${bubbleIntensity * 0.2})`,
                    )

                    ctx.fillStyle = bubbleGradient
                    ctx.globalAlpha = bubbleIntensity * 0.7
                    ctx.beginPath()
                    ctx.arc(bubbleX, bubbleY, bubbleRadius, 0, Math.PI * 2)
                    ctx.fill()

                    // Bubble highlight
                    ctx.fillStyle = `hsla(${bubbleHue}, 100%, 80%, ${bubbleIntensity * 0.3})`
                    ctx.globalAlpha = bubbleIntensity * 0.6
                    ctx.beginPath()
                    ctx.arc(
                        bubbleX - bubbleRadius * 0.3,
                        bubbleY - bubbleRadius * 0.3,
                        bubbleRadius * 0.4,
                        0,
                        Math.PI * 2,
                    )
                    ctx.fill()
                }

                ctx.restore()
            })

            animationFrameRef.current = requestAnimationFrame(animate)
        }

        const animationFrameRef = { current: 0 as number }
        animate()

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current)
            }
        }
    }, [clickPower, canvasRef, timeRef])

    return null
}
